import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IFavoritesParams } from '~/src/repositories/otherRepository/models/Favorites'

interface IFavoriteItem {
  ref?: string
  name?: string
  type?: string
  favorites?: number
}

interface IColumn {
  id: 'ref' | 'name' | 'type' | 'favorites'
  label: string
  minWidth?: number
  align?: 'right'
  render: (item: IFavoriteItem, index: number) => React.ReactNode
}

interface TablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void
}
interface IFavoritesProps {
  data?: IFavoriteItem[]
  pagination: {
    currentPage: number
    limitValue: number
    totalCount: number
    totalPages: number
  }
  onNextPage: (values: IFavoritesParams) => void
  maxHeight?: string
}
function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme()
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const { count, page, rowsPerPage, onPageChange } = props

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}

export const Favorites: React.FC<IFavoritesProps> = ({
  data = [],
  pagination,
  onNextPage,
  maxHeight = '275px',
}): JSX.Element => {
  const { t } = useTranslation()
  const [page, setPage] = useState(pagination.currentPage - 1)
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(20)

  const columns: IColumn[] = [
    {
      id: 'ref',
      label: `${t('favorites.ref_no.')}`,
      minWidth: 170,
      render: (item) => (
        <Typography fontWeight={400} fontStyle={'normal'} fontSize={14} lineHeight={'20.02px'}>
          {item.ref}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: `${t('favorites.product_name')}`,
      minWidth: 400,
      render: (item) => (
        <Typography fontWeight={400} fontStyle={'normal'} fontSize={14} lineHeight={'20.02px'}>
          {item.name}
        </Typography>
      ),
    },
    {
      id: 'type',
      label: `${t('favorites.product_type')}`,
      minWidth: 170,
      render: (item) => (
        <Typography fontWeight={400} fontStyle={'normal'} fontSize={14} lineHeight={'20.02px'}>
          {item.type}
        </Typography>
      ),
    },
    {
      id: 'favorites',
      label: `${t('favorites.number_of_favorites')}`,
      minWidth: 170,
      render: (item) => (
        <Typography fontWeight={400} fontStyle={'normal'} fontSize={14} lineHeight={'20.02px'}>
          {item.favorites}
        </Typography>
      ),
    },
  ]

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage)
    onNextPage({ page: newPage })
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight, boxShadow: 'none' }}>
        <Table
          sx={{ maxHeight: 440 }}
          aria-label="custom pagination table sticky table"
          aria-labelledby="tableTitle"
          size={'medium'}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth, background: 'white' }}
                  padding={'normal'}
                >
                  <Typography
                    sx={{
                      borderRight: 2,
                      borderColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}`,
                      fontSize: 14,
                      lineHeight: '20px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      height: '14px',
                    }}
                  >
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              return (
                <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                  {columns.map((column, i) => {
                    return (
                      <TableCell key={i} align={column.align} sx={{ p: '13px' }}>
                        {column.render(item, index)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[]}
                count={pagination?.totalCount || 0}
                sx={{
                  fontSize: 12,
                  fontStyle: 'normal',
                  lineHeight: '19.92px',
                  fontWeight: 400,
                }}
                rowsPerPage={rowsPerPage || 0}
                page={page}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  )
}
